import React, { useMemo } from 'react';
import { breakpoint } from '../../styling/mixins/breakpoints';
import { getColor } from '../../styling/mixins/colors';
import ProductFiltering from '../ProductFiltering/ProductFiltering';
import SideMenu from '../SideMenu/SideMenu';
import styled, { createGlobalStyle } from 'styled-components';
import { translate } from '../../Services/translation';
import ProductSortingRadios from './ProductSortingRadios';
import { text, fontFamilyBold } from '../../styling/mixins/typography';
import Button from '../Button';
import { clearAllFilters } from '../../Actions/ProductFiltering.action';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import CategoriesMenu from './CategoriesMenu';

const StyledFilterMenu = styled(SideMenu)`
  height: calc(100% - 67px);
> div:first-of-type{
  background: ${getColor('FFFFFF')};
  }
  && {
    z-index: 60;
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    ${breakpoint('medium')} {
      max-width: 360px;
    }
  }
`;

const StyledProductFiltering = styled.div`
  & .collapsible {
    border-top: none;
    border-bottom: 1px solid ${getColor('grey', 300)};

    &:last-child {
      border: none;
    }

    & label {
      ${text('xlarge')}
      ${fontFamilyBold}
      padding: 1.6rem;
    }
  }

  & .ReactCollapse--collapse[aria-hidden='false'] .ReactCollapse--content {
    padding-left: 1.6rem;
    padding-right: 1.6rem;

    & label {
      padding: 0.4rem 0;
    }
    & button {
      margin-top: 1.6rem;
    }
  }
`;

const StyledProductFilteringFooter = styled.div`
  display: flex;
  flex: 1;
  gap: 0.8rem;

  button {
    ${text('medium')}
    ${fontFamilyBold};
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .clear-button {
    border-color: ${getColor('grey', 300)};
    color: ${getColor('grey', 900)};
    opacity: 1;
  }
`;

const GlobalFilterMenuStyling = createGlobalStyle`
  body #overlay-container .overlay--filter-menu {
    z-index: 50;
  }
`;

const FilterMenu = ({ currentMenu, setCurrentMenu }) => {
  const dispatch = useDispatch();
  const { totalProductsCount } = useTypedSelector(
    (state) => state.productListing
  );
  const filters = useTypedSelector((state) => state.productFiltering.filters);
  const hasAnyOptionSelected = useMemo(
    () => filters?.findIndex((f) => f.selectedOptions.length > 0) > 0,
    [filters]
  );

  const Header = () => (
    <>
      {currentMenu == 'categories' ? (
        <h4>{translate('filter.categories')}</h4>
      ) : null}
      {currentMenu == 'filters' ? <h4>{translate('filter.filter')}</h4> : null}
      {currentMenu == 'sorting' ? <h4>{translate('filter.sort')}</h4> : null}
    </>
  );

  const Content = () => (
    <>
      {currentMenu == 'categories' ? <CategoriesMenu /> : null}
      {currentMenu == 'filters' ? (
        <StyledProductFiltering>
          <ProductFiltering />
        </StyledProductFiltering>
      ) : null}
      {currentMenu == 'sorting' ? <ProductSortingRadios /> : null}
    </>
  );

  const Footer = () => (
    <>
      {currentMenu == 'filters' ? (
        <StyledProductFilteringFooter>
          <Button
            btnType="outlined"
            btnSize="large"
            fullwidth={true}
            disabled={!hasAnyOptionSelected}
            onClick={() => dispatch(clearAllFilters())}
            className="clear-button"
          >
            {translate('filter.clear')}
          </Button>
          <Button
            btnType="primary"
            btnSize="large"
            fullwidth={true}
            onClick={() => setCurrentMenu(undefined)}
          >
            {translate('filter.view')} ( {totalProductsCount} )
          </Button>
        </StyledProductFilteringFooter>
      ) : null}
    </>
  );

  return (
    <>
      <StyledFilterMenu
        from="right"
        id="filter-menu"
        open={!!currentMenu}
        onClosed={() => setCurrentMenu(undefined)}
        components={{ Header, Content, Footer }}
      ></StyledFilterMenu>
      <GlobalFilterMenuStyling />
    </>
  );
};

export default FilterMenu;
